/**
 * APPLYING ONE FIELD EDIT TO THE UNSAVED EDITS — every rule asked of the rules util (ADR 0009), every field
 * shape built by the form's builders, so the chat can do exactly what the form can and nothing more.
 * MUTATES `draft`: the router hands in a working clone and throws it away if nothing applied.
 * A refused part is named in `declined` while the rest applies (Q10, ADR 0005).
 * ⚠ `import type` only — see rules/entity-form-rules.ts.
 */
import type { Entity, TypeData } from '../../../../model/entity.model';
import type { EntityEdit } from '../zs-entity.types';
// Was (2026-10-03, final review — C1 canOpenFieldForEdit, M4 cleanFieldNameOnAdd):
// import {
//   canEditAllFieldSettings, checkRenamedFieldName, cleanFieldNameOnEdit, isSystemField,
//   lockedFieldEditableSettings, offeredFieldTypes, prepareNewFieldName, referenceTargetOptions, referenceTargets,
// } from '../rules/entity-form-rules';
import {
  canEditAllFieldSettings, canHaveSampleRows, canOpenFieldForEdit, checkRenamedFieldName, cleanFieldNameOnAdd, cleanFieldNameOnEdit,
  isSystemField, lockedFieldEditableSettings, offeredFieldTypes, prepareNewFieldName, referenceTargetOptions, referenceTargets,
  sampleRowFields,
} from '../rules/entity-form-rules';
import { aobSystemFields, applyTypeDefaults, blankField, extendedAttributeFields, listFields, sampleRowSeed } from '../rules/field-builders';
import { typeLabel } from '../concepts/type-vocabulary';
import { instantFromWallInput, wallInputFromInstant } from '../../../../shared/time/project-clock';

export interface ApplyContext {
  /** Every entity the page loaded (hidden included) — for links. */
  entities: readonly Entity[];
  /** Mints a field id suffix — the page passes its own `generateTableId`. */
  mint: () => string;
  /** Seeds an extended-attribute list's `systemTimestamp`. */
  nowIso: string;
  /** How a link's `reference` names its entity. The chat names it; the Claude tools pass an id. Default: by name. */
  linkTarget?: LinkLookup;
}

export type LinkLookup = (reference: string | null, draft: Entity, entities: readonly Entity[]) => { name: string; id: string } | string;

export interface EditOutcome {
  /** One line per change made, in builders' words. */
  changes: string[];
  /** One line per part refused, with the reason. */
  declined: string[];
}

interface Container { data: TypeData[]; parentId: string; label: string }

/** The field list at `path` (list names, outermost first), or the reason there is none. */
function containerAt(draft: Entity, path: readonly string[]): Container | string {
  let data = draft.data;
  let parentId = draft.id;
  let label = draft.name;
  for (const step of path) {
    // Was (2026-10-03, final review — M4, missed the name Add cleaned, e.g. line_items → lineitems):
    // const list = data.find((f) => f.type === 'ARRAY_OF_OBJECT' && f.name.toLowerCase() === step.toLowerCase());
    const list = data.find((f) => f.type === 'ARRAY_OF_OBJECT'
      && (f.name.toLowerCase() === step.toLowerCase() || f.name === cleanFieldNameOnAdd(step)));
    if (!list) return `${label} has no list called ${step}.`;
    if (!Array.isArray(list.data)) list.data = [];
    data = list.data;
    parentId = list.id || parentId; // the form's own rule: the container field's id, else the entity's
    label = `${label} › ${list.name}`;
  }
  return { data, parentId, label };
}

/** A link's target among the finalized entities the form would offer, or the reason it can't be. */
// Was (2026-10-29, the Claude tools add fields through this file and look a link up by id):
// function linkTarget(name: string | null, draft: Entity, entities: readonly Entity[]): { name: string; id: string } | string {
export function linkTargetByName(name: string | null, draft: Entity, entities: readonly Entity[]): { name: string; id: string } | string {
  if (!name) return 'A link needs the entity it points to.';
  const wanted = name.toLowerCase();
  const hit = referenceTargetOptions(referenceTargets(entities), draft.id).find((t) => t.name.toLowerCase() === wanted);
  if (hit) return hit;
  // Was (2026-10-16, self-reference — S9 allows it): if (wanted === draft.name.toLowerCase()) return 'An entity can\'t link to itself here.';
  const known = entities.find((e) => !e.hidden && e.name.toLowerCase() === wanted);
  if (known?.status === 'LOCAL') return `${known.name} is a local entity, so nothing can link to it.`;
  if (known) return `${known.name} isn't finalized yet, so nothing can link to it. Finalize ${known.name} first.`;
  return `There's no finalized entity called ${name} to link to.`;
}

/** The same target, named by entity id: the same offer, the same reasons. */
export function linkTargetById(id: string | null, draft: Entity, entities: readonly Entity[]): { name: string; id: string } | string {
  if (!id) return 'A link needs the entity it points to.';
  const hit = referenceTargetOptions(referenceTargets(entities), draft.id).find((t) => t.id === id);
  if (hit) return { name: hit.name, id: hit.id };
  const known = entities.find((e) => !e.hidden && e.id === id);
  if (known?.status === 'LOCAL') return `${known.name} is a local entity, so nothing can link to it.`;
  if (known) return `${known.name} isn't finalized yet, so nothing can link to it. Finalize ${known.name} first.`;
  return `There's no finalized entity with id ${id} to link to.`;
}

const notOffered = (draft: Entity, type: string): string =>
  draft.status === 'LOCAL'
    ? `${draft.name} is a local entity, so it can't hold a ${typeLabel(type).toLowerCase()}.`
    : `${typeLabel(type)} isn't a field type the entity page offers.`;

/** Give a list of items its system fields (and extended attributes), as the form's Add does. */
function makeList(field: TypeData, parentId: string, extended: boolean, nowIso: string): void {
  field.extendedAttributes = extended;
  // Was (2026-10-04, plan 4c Q41 — one shared builder for Add, Edit and the chat):
  // field.data = aobSystemFields(parentId);
  // if (extended) field.data.push(...extendedAttributeFields(nowIso));
  field.data = listFields(parentId, extended, nowIso);
}

const SETTING_WORDS: Record<string, string> = {
  defaultValue: 'default', defaultMode: 'default mode', description: 'description',
};
const andList = (xs: string[]): string => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs.at(-1)}`);

const TRUE_WORDS = new Set(['true', 'yes', 'on']);
const FALSE_WORDS = new Set(['false', 'no', 'off']);

/**
 * A fixed default the way the form's datetime box stores it: the PROJECT's wall clock, to the minute.
 * A value with a zone (Z / +05:30) is that moment; one without is a wall-clock time in the project's zone,
 * never the browser's (the 2026-09-19 fix). A date alone means midnight. '' when it can't be read.
 */
function fixedInstant(v: unknown): string {
  if (typeof v === 'number') return instantFromWallInput(wallInputFromInstant(v));
  if (typeof v !== 'string') return '';
  const s = v.trim().replace(' ', 'T');
  const wall = /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00` : s;
  return instantFromWallInput(wallInputFromInstant(wall));
}

/** A text, number or yes/no value, or undefined when it can't be one. Shared by defaults and sample-row cells. */
function plainValue(type: string, v: string | number | boolean): string | number | boolean | undefined {
  switch (type) {
    case 'STRING':
      return String(v);
    case 'NUMBER': {
      const n = typeof v === 'number' ? v : Number(String(v).trim());
      return String(v).trim() === '' || !Number.isFinite(n) ? undefined : n;
    }
    case 'BOOLEAN': {
      if (typeof v === 'boolean') return v;
      const s = String(v).trim().toLowerCase();
      return TRUE_WORDS.has(s) ? true : FALSE_WORDS.has(s) ? false : undefined;
    }
    default:
      return undefined;
  }
}

/**
 * Set a field's default from an edit. Text, number, yes/no and date-and-time only; any other type is named
 * as a gap and left to the field form. Returns the problem, or null (including when nothing was asked).
 */
export function applyDefault(field: TypeData, edit: Pick<EntityEdit, 'defaultValue' | 'defaultMode'>): string | null {
  const v = edit.defaultValue;
  const mode = edit.defaultMode;
  if (v === null && mode === null) return null;
  if (field.type === 'TIMESTAMP') {
    if (mode === 'now' || mode === 'none') { field.defaultMode = mode; return null; }
    // Was (2026-10-03, review — browser time zone, not the project clock):
    // const t = typeof v === 'string' || typeof v === 'number' ? new Date(v) : null;
    // if (!t || Number.isNaN(t.getTime())) return `${field.name}'s fixed default needs a date and time.`;
    // field.defaultValue = t.toISOString();
    const iso = fixedInstant(v);
    if (!iso) return `${field.name}'s fixed default needs a date and time.`;
    field.defaultMode = 'fixed';
    field.defaultValue = iso;
    return null;
  }
  if (mode !== null) return `Only a date and time field has a default mode; ${field.name} is ${typeLabel(field.type).toLowerCase()}.`;
  switch (field.type) {
    // Was (2026-10-03, plan 4a — coercion shared with sample-row cells):
    // case 'STRING':
    //   field.defaultValue = String(v);
    //   return null;
    // case 'NUMBER': {
    //   const n = typeof v === 'number' ? v : Number(String(v).trim());
    //   if (String(v).trim() === '' || !Number.isFinite(n)) return `${field.name}'s default must be a number.`;
    //   field.defaultValue = n;
    //   return null;
    // }
    // case 'BOOLEAN': {
    //   const s = String(v).trim().toLowerCase();
    //   if (typeof v !== 'boolean' && !TRUE_WORDS.has(s) && !FALSE_WORDS.has(s)) return `${field.name}'s default must be yes or no.`;
    //   field.defaultValue = typeof v === 'boolean' ? v : TRUE_WORDS.has(s);
    //   return null;
    // }
    case 'STRING':
    case 'NUMBER':
    case 'BOOLEAN': {
      const x = plainValue(field.type, v as string | number | boolean);
      if (x === undefined) return field.type === 'NUMBER' ? `${field.name}'s default must be a number.` : `${field.name}'s default must be yes or no.`;
      field.defaultValue = x;
      return null;
    }
    default:
      // Was (2026-10-03, final review — T5, pointed at a form that has no default for these types either):
      // return `I can't set a default for ${field.name} (${typeLabel(field.type).toLowerCase()}) here — use the field form.`;
      return `${field.name} is a ${typeLabel(field.type).toLowerCase()} field, which has no default on the entity page.`;
  }
}

function addField(draft: Entity, edit: EntityEdit, ctx: ApplyContext): EditOutcome {
  const out: EditOutcome = { changes: [], declined: [] };
  const c = containerAt(draft, edit.path);
  if (typeof c === 'string') return { changes: [], declined: [c] };
  const { name, check } = prepareNewFieldName(edit.field ?? '', c.data);
  if (!check.ok) return { changes: [], declined: [`Couldn't add ${edit.field}: ${check.reason}`] };
  // Was (2026-10-03, final review — M3, a link with no type became text):
  // const type = edit.type ?? 'STRING';
  const type = edit.type ?? (edit.reference ? 'ENTITY_REFERENCE' : 'STRING');
  if (!offeredFieldTypes(draft.status, null).includes(type)) return { changes: [], declined: [notOffered(draft, type)] };
  const field = applyTypeDefaults({ ...blankField(), name, type }).field;
  let linked = '';
  if (type === 'ENTITY_REFERENCE') {
    const target = (ctx.linkTarget ?? linkTargetByName)(edit.reference, draft, ctx.entities);
    if (typeof target === 'string') return { changes: [], declined: [target] };
    field.referenceEntityName = target.id;
    linked = ` → ${target.name}`;
  }
  if (type === 'ARRAY_OF_OBJECT') makeList(field, c.parentId, !!edit.extendedAttributes, ctx.nowIso);
  if (edit.description) field.description = edit.description;
  const problem = applyDefault(field, edit);
  if (problem) out.declined.push(problem);
  field.id = `${name}_${ctx.mint()}`;
  c.data.push(field);
  const where = edit.path.length ? ` to ${c.label}` : '';
  out.changes.unshift(`Added ${name} (${typeLabel(type)}${linked})${where}`);
  return out;
}

function editField(draft: Entity, edit: EntityEdit, ctx: ApplyContext): EditOutcome {
  const out: EditOutcome = { changes: [], declined: [] };
  const c = containerAt(draft, edit.path);
  if (typeof c === 'string') return { changes: [], declined: [c] };
  const wanted = (edit.field ?? '').toLowerCase();
  // Was (2026-10-03, final review — M4, missed the name Add cleaned):
  // const idx = c.data.findIndex((f) => f.name.toLowerCase() === wanted);
  const idx = c.data.findIndex((f) => f.name.toLowerCase() === wanted || f.name === cleanFieldNameOnAdd(edit.field ?? ''));
  if (idx < 0) return { changes: [], declined: [`${c.label} has no field called ${edit.field}.`] };
  const field = c.data[idx];
  if (isSystemField(field.name)) return { changes: [], declined: [`${field.name} is managed by Zavo and can't be changed.`] };
  // C1 (2026-10-03, final review): the page never opens an existing list for editing.
  if (!canOpenFieldForEdit(field)) {
    return { changes: [], declined: [`${field.name} is a list of items, which the entity page can't edit — change the fields inside it instead.`] };
  }
  const oldName = field.name;
  const everything = canEditAllFieldSettings(draft);
  // Was (2026-10-03, final review — M2, echoing the current name or link was refused as a change):
  // const retype = edit.type !== null && edit.type !== field.type;
  // const relink = edit.reference !== null;
  // const structural = edit.newName !== null || retype || relink || edit.extendedAttributes !== null;
  const retype = edit.type !== null && edit.type !== field.type;
  const currentTarget = ctx.entities.find((e) => e.id === field.referenceEntityName)?.name ?? null;
  const relink = edit.reference !== null
    && !(field.type === 'ENTITY_REFERENCE' && currentTarget !== null && currentTarget.toLowerCase() === edit.reference.toLowerCase());
  const rename = edit.newName !== null && cleanFieldNameOnEdit(edit.newName) !== field.name;
  const structural = rename || retype || relink || edit.extendedAttributes !== null;

  if (structural && !everything) {
    const allowed = lockedFieldEditableSettings(field).map((k) => SETTING_WORDS[k] ?? k);
    out.declined.push(`${draft.name} is already finalized, so ${oldName} can only change its ${andList(allowed)}.`);
  } else if (structural) {
    if (retype) {
      const type = edit.type as string;
      if (!offeredFieldTypes(draft.status, null).includes(type)) {
        out.declined.push(notOffered(draft, type));
      } else {
        let target: { name: string; id: string } | string | null = null;
        if (type === 'ENTITY_REFERENCE') target = (ctx.linkTarget ?? linkTargetByName)(edit.reference, draft, ctx.entities);
        if (typeof target === 'string') {
          out.declined.push(target);
        } else {
          Object.assign(field, applyTypeDefaults({ ...field, type }).field);
          if (target) field.referenceEntityName = target.id;
          // Was (2026-10-03, review — pointed a new list's own id at itself instead of its container):
          // if (type === 'ARRAY_OF_OBJECT') makeList(field, field.id || c.parentId, !!edit.extendedAttributes, ctx.nowIso);
          if (type === 'ARRAY_OF_OBJECT') makeList(field, c.parentId, !!edit.extendedAttributes, ctx.nowIso);
          out.changes.push(`${oldName} is now ${typeLabel(type)}${target ? ` → ${target.name}` : ''}`);
        }
      }
    } else if (relink) {
      if (field.type !== 'ENTITY_REFERENCE') {
        out.declined.push(`${oldName} isn't a link, so it can't point at ${edit.reference}.`);
      } else {
        const target = (ctx.linkTarget ?? linkTargetByName)(edit.reference, draft, ctx.entities);
        if (typeof target === 'string') out.declined.push(target);
        else { field.referenceEntityName = target.id; out.changes.push(`${oldName} now links to ${target.name}`); }
      }
    }
    if (edit.extendedAttributes !== null && !retype) {
      if (field.type !== 'ARRAY_OF_OBJECT') {
        out.declined.push(`${oldName} isn't a list of items, so it has no extended attributes.`);
      // Unreachable for lists since the final review (C1): a list can't be opened for editing. Kept for the non-list refusal.
      } else if (!!field.extendedAttributes !== edit.extendedAttributes) {
        // ⚠ N14 — as the form: the flag only; the six extended-attribute columns are not added or removed.
        field.extendedAttributes = edit.extendedAttributes;
        out.changes.push(`${oldName}: extended attributes ${edit.extendedAttributes ? 'on' : 'off'}`);
      }
    }
    // Was (2026-10-03, final review — M2):
    // if (edit.newName !== null) {
    //   const name = cleanFieldNameOnEdit(edit.newName);
    if (rename) {
      const name = cleanFieldNameOnEdit(edit.newName as string);
      const check = checkRenamedFieldName(name, field.name, c.data, idx);
      if (!check.ok) out.declined.push(`Couldn't rename ${oldName}: ${check.reason}`);
      else if (name !== field.name) { field.name = name; out.changes.push(`Renamed ${oldName} to ${name}`); }
    }
  }

  if (edit.description !== null) {
    field.description = edit.description;
    out.changes.push(`Described ${field.name}`);
  }
  if (edit.defaultValue !== null || edit.defaultMode !== null) {
    const problem = applyDefault(field, edit);
    if (problem) out.declined.push(problem);
    else out.changes.push(`Set ${field.name}'s default`);
  }
  if (!out.changes.length && !out.declined.length) out.declined.push(`Nothing to change on ${oldName}.`);
  return out;
}

// ── Sample rows (plan 4a) — a LOCAL entity's predefinedData, edited in the unsaved edits ──────────────

export const NO_SAMPLE_ROWS_TEXT = (name: string): string =>
  `${name || 'This entity'} has no sample rows — only a local entity with plain fields carries them.`;

type CellIn = string | number | boolean | null;

/** A cell value for a sample-row column, or the reason it can't be one. */
function cellValue(field: TypeData, v: CellIn): { value: unknown } | { problem: string } {
  // Was (2026-10-03, fix round 1 — FIX 1, parity with the Predefined modal's cleared-cell storage):
  // if (v === null) return { value: null };
  // What the Predefined modal stores for a cleared cell: '' for text and dates, false for yes/no, null for a number.
  if (v === null) return { value: field.type === 'NUMBER' ? null : field.type === 'BOOLEAN' ? false : '' };
  if (field.type === 'TIMESTAMP') {
    const iso = fixedInstant(v);
    return iso ? { value: iso } : { problem: `${field.name} needs a date and time.` };
  }
  const x = plainValue(field.type, v);
  if (x === undefined) return { problem: field.type === 'NUMBER' ? `${field.name} must be a number.` : `${field.name} must be yes or no.` };
  return { value: x };
}

// Was (2026-10-03, fix round 1 — FIX 2, the change line now names the cells that landed):
// /** Write `values` into `row`; returns how many cells landed. Unknown columns and bad values go to `out.declined`. */
// function fillCells(draft: Entity, row: Record<string, unknown>, values: Record<string, CellIn> | null, fields: TypeData[], out: EditOutcome): number {
//   let landed = 0;
//   for (const [name, v] of Object.entries(values ?? {})) {
//     const field = fields.find((f) => f.name.toLowerCase() === name.toLowerCase());
//     if (!field) { out.declined.push(`${draft.name} has no sample-row column called ${name}.`); continue; }
//     const cell = cellValue(field, v);
//     if ('problem' in cell) out.declined.push(cell.problem);
//     else { row[field.name] = cell.value; landed++; }
//   }
//   return landed;
// }
/** Write `values` into `row`; returns the cells that landed (field name → stored value). Unknown columns and bad values go to `out.declined`. */
function fillCells(draft: Entity, row: Record<string, unknown>, values: Record<string, CellIn> | null, fields: TypeData[], out: EditOutcome): Record<string, unknown> {
  const landed: Record<string, unknown> = {};
  for (const [name, v] of Object.entries(values ?? {})) {
    const field = fields.find((f) => f.name.toLowerCase() === name.toLowerCase());
    if (!field) { out.declined.push(`${draft.name} has no sample-row column called ${name}.`); continue; }
    const cell = cellValue(field, v);
    if ('problem' in cell) out.declined.push(cell.problem);
    else { row[field.name] = cell.value; landed[field.name] = cell.value; }
  }
  return landed;
}

/** The row an edit points at: by position (1 = first) or by a cell's value (as stored, else case-insensitive text); -1 if none. */
function rowIndexOf(rows: Record<string, unknown>[], edit: EntityEdit, fields: TypeData[]): number {
  if (edit.rowNumber !== null) return edit.rowNumber <= rows.length ? edit.rowNumber - 1 : -1;
  if (!edit.row) return -1;
  const field = fields.find((f) => f.name.toLowerCase() === edit.row!.field.toLowerCase());
  const key = field?.name ?? edit.row.field;
  // MINOR A (2026-10-03, fix round 1): compare the picked value as the column stores it, when the column is known.
  if (field) {
    const picked = cellValue(field, edit.row.value);
    if ('value' in picked) {
      return rows.findIndex((r) => (typeof picked.value === 'string'
        ? String(r[key] ?? '').toLowerCase() === picked.value.toLowerCase()
        : r[key] === picked.value));
    }
  }
  const wanted = String(edit.row.value).toLowerCase();
  return rows.findIndex((r) => String(r[key] ?? '').toLowerCase() === wanted);
}

const whichRow = (edit: EntityEdit): string =>
  edit.rowNumber !== null ? String(edit.rowNumber) : `where ${edit.row?.field} is ${edit.row?.value}`;
/** A landed cell's value in the change line's words: '(blank)' for the cleared-cell storage, else the text form. */
const shown = (v: unknown): string => (v === '' ? '(blank)' : String(v));
const cellsText = (landed: Record<string, unknown>): string =>
  Object.entries(landed).map(([k, v]) => `${k}: ${shown(v)}`).join(', ');

function addRow(draft: Entity, edit: EntityEdit): EditOutcome {
  if (!canHaveSampleRows(draft)) return { changes: [], declined: [NO_SAMPLE_ROWS_TEXT(draft.name)] };
  const fields = sampleRowFields(draft.data);
  const row = sampleRowSeed(fields);
  const out: EditOutcome = { changes: [], declined: [] };
  const landed = fillCells(draft, row, edit.values, fields, out);
  if (!Array.isArray(draft.predefinedData)) draft.predefinedData = [];
  draft.predefinedData.push(row);
  const given = cellsText(landed);
  out.changes.unshift(`Added a sample row${given ? ` (${given})` : ''}`);
  return out;
}

function editRow(draft: Entity, edit: EntityEdit): EditOutcome {
  if (!canHaveSampleRows(draft)) return { changes: [], declined: [NO_SAMPLE_ROWS_TEXT(draft.name)] };
  const fields = sampleRowFields(draft.data);
  const rows = draft.predefinedData ?? [];
  const i = rowIndexOf(rows, edit, fields);
  if (i < 0) return { changes: [], declined: [`${draft.name} has no sample row ${whichRow(edit)}.`] };
  const out: EditOutcome = { changes: [], declined: [] };
  const landed = fillCells(draft, rows[i], edit.values, fields, out);
  if (Object.keys(landed).length > 0) out.changes.unshift(`Changed sample row ${i + 1}`);
  return out;
}

function removeRow(draft: Entity, edit: EntityEdit): EditOutcome {
  if (!canHaveSampleRows(draft)) return { changes: [], declined: [NO_SAMPLE_ROWS_TEXT(draft.name)] };
  const rows = draft.predefinedData ?? [];
  const i = rowIndexOf(rows, edit, sampleRowFields(draft.data));
  if (i < 0) return { changes: [], declined: [`${draft.name} has no sample row ${whichRow(edit)}.`] };
  rows.splice(i, 1);
  return { changes: [`Removed sample row ${i + 1}`], declined: [] };
}

/** add_field or edit_field on `draft`. create_entity is the router's (it needs the skeleton and the name check). */
export function applyFieldEdit(draft: Entity, edit: EntityEdit, ctx: ApplyContext): EditOutcome {
  if (edit.op === 'add_field') return addField(draft, edit, ctx);
  if (edit.op === 'edit_field') return editField(draft, edit, ctx);
  if (edit.op === 'add_row') return addRow(draft, edit);
  if (edit.op === 'edit_row') return editRow(draft, edit);
  if (edit.op === 'remove_row') return removeRow(draft, edit);
  return { changes: [], declined: [`${edit.entity ?? 'That entity'} is created by the router, not here.`] };
}
